import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import EncargoClausulasEditor from "../components/EncargoClausulasEditor";
import DEFAULT_ENCARGO_CLAUSULAS from "../constants/encargo-clausulas-default.json";
import {
  MAX_ENCARGO_CLAUSULAS,
  parseEncargoFilasDesdeContenido,
  serializarEncargoFilasJson,
} from "../utils/encargoClausulasVisual";
import api from "../services/api";
import usePermiso from "../hooks/usePermiso";

function slugValido(s) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(s || "").trim());
}

const SLUGS_ENCARGO_CLAUSULAS = [
  "encargo-venta-mediacional",
  "encargo-venta-5-porciento",
  "encargo-venta-4-porciento",
  "encargo-exclusiva-venta",
  "encargo-exclusiva-venta-5",
  "encargo-exclusiva-venta-4",
];

function esEditorEncargoClausulas(slug) {
  return SLUGS_ENCARGO_CLAUSULAS.includes(slug);
}

export default function PlantillasDocumentacion() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { puede } = usePermiso();
  const verClausulas = puede("pla_doc_clausulas", "ver");
  const modClausulas = puede("pla_doc_clausulas", "modificar");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [editSlug, setEditSlug] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [contenido, setContenido] = useState("");
  /** Filas del editor visual de encargo (solo slugs encargo-venta-*). */
  const [encargoFilas, setEncargoFilas] = useState([]);
  const [nuevoSlug, setNuevoSlug] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/documentacion");
      if (data.ok) setItems(data.items || []);
      else setErr(data.error || "Error al cargar");
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Error de red");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirEditor = async (slug) => {
    setMsg("");
    setErr("");
    setEditSlug(slug);
    try {
      const { data } = await api.get(`/documentacion/item/${encodeURIComponent(slug)}`);
      if (!data.ok) {
        setErr(data.error || "Error");
        return;
      }
      const it = data.item || {};
      setTitulo(it.titulo || slug);
      const raw = it.contenido != null ? String(it.contenido) : "";
      setContenido(raw);
      if (esEditorEncargoClausulas(slug)) {
        const parsed = parseEncargoFilasDesdeContenido(raw);
        setEncargoFilas(
          parsed && parsed.length
            ? parsed
            : JSON.parse(JSON.stringify(DEFAULT_ENCARGO_CLAUSULAS))
        );
      } else {
        setEncargoFilas([]);
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  };

  const guardar = async () => {
    if (!editSlug) return;
    setGuardando(true);
    setMsg("");
    setErr("");
    try {
      if (esEditorEncargoClausulas(editSlug) && encargoFilas.length > MAX_ENCARGO_CLAUSULAS) {
        setErr(`Como máximo ${MAX_ENCARGO_CLAUSULAS} cláusulas.`);
        setGuardando(false);
        return;
      }
      const contenidoPayload = esEditorEncargoClausulas(editSlug)
        ? serializarEncargoFilasJson(encargoFilas)
        : contenido;

      const { data } = await api.put(`/documentacion/item/${encodeURIComponent(editSlug)}`, {
        titulo,
        contenido: contenidoPayload,
      });
      if (!data.ok) {
        setErr(data.error || "No se pudo guardar");
        return;
      }
      setMsg(
        esEditorEncargoClausulas(editSlug)
          ? "Guardado. El próximo PDF de encargo (previsualización, envío y firma ViaFirma) usará estas cláusulas. Tras firmar, el PDF firmado se archiva en Documentación de la captación/propiedad (tipo según encargo) y en NAS Referencias/documentacion/."
          : "Guardado. La próxima impresión usará este texto."
      );
      await cargar();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const crearNuevo = async () => {
    const s = nuevoSlug.trim().toLowerCase();
    if (!slugValido(s)) {
      setErr("Slug: solo minúsculas, números y guiones (ej. aviso-visitas).");
      return;
    }
    setNuevoSlug("");
    await abrirEditor(s);
    setTitulo(s);
    setContenido("");
    setEncargoFilas([]);
    setMsg("Nuevo documento: rellena título y texto, luego Guardar.");
  };

  return (
    <div className="min-h-screen" style={{ background: c.bgApp }}>
      <div
        style={{
          background: c.bgCard,
          borderBottom: `1px solid ${c.border}`,
          padding: "28px 32px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg,#0f766e,#14b8a6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            📄
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: c.textPrimary }}>
              Documentación
            </h1>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: c.textMuted, lineHeight: 1.55 }}>
              Cada tipo de encargo del móvil tiene su documento:{" "}
              <code>encargo-venta-mediacional</code>, <code>encargo-venta-5-porciento</code>,{" "}
              <code>encargo-venta-4-porciento</code>, <code>encargo-exclusiva-venta</code> (opción antes de firmar),{" "}
              <code>encargo-exclusiva-venta-5</code> y <code>encargo-exclusiva-venta-4</code>. Al guardar se genera el JSON para el PDF y
              ViaFirma. Puedes añadir otros slugs para fragmentos HTML en plantillas de impresión con{" "}
              <code style={{ fontSize: 12, background: theme.name === "dark" ? "#1e293b" : "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                {"{{DOCUMENTACION:tu-slug}}"}
              </code>{" "}
              (<Link to="/plantillas/impresion" style={{ color: c.accentBlue }}>
                plantillas de impresión
              </Link>
              ); si el slug no existe, al imprimir ese hueco quedará vacío.
            </p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 48px", display: "grid", gap: 24 }}>
        {err && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.35)",
              color: "#b91c1c",
              fontSize: 14,
            }}
          >
            {err}
          </div>
        )}
        {msg && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(16,185,129,0.12)",
              border: "1px solid rgba(16,185,129,0.35)",
              color: "#047857",
              fontSize: 14,
            }}
          >
            {msg}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 340px) 1fr",
            gap: 20,
            alignItems: "start",
          }}
          className="doc-grid"
        >
          <div
            style={{
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 16,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: c.textPrimary, marginBottom: 10 }}>Documentos</div>
            {loading ? (
              <p style={{ color: c.textMuted, fontSize: 13 }}>Cargando…</p>
            ) : items.length === 0 ? (
              <p style={{ color: c.textMuted, fontSize: 13 }}>Ninguno aún. Crea uno con el formulario de abajo.</p>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((row) => (
                  <li key={row.slug} style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => abrirEditor(row.slug)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        borderRadius: 10,
                        border:
                          editSlug === row.slug
                            ? `2px solid ${c.accentBlue}`
                            : `1px solid ${c.border}`,
                        background: editSlug === row.slug ? c.accentBlueLight : "transparent",
                        color: c.textPrimary,
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{row.titulo}</div>
                      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                        <code>{row.slug}</code> · {row.caracteres || 0} caracteres
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${c.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 6 }}>Nuevo slug</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  value={nuevoSlug}
                  onChange={(e) => setNuevoSlug(e.target.value)}
                  placeholder="ej. aviso-legal-extra"
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                    background: c.bgApp,
                    color: c.textPrimary,
                    fontSize: 13,
                  }}
                />
                <button
                  type="button"
                  onClick={crearNuevo}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: c.accentBlue,
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Crear / editar
                </button>
              </div>
            </div>
          </div>

          <div
            style={{
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              padding: 18,
            }}
          >
            {!editSlug ? (
              <p style={{ color: c.textMuted, margin: 0, fontSize: 14 }}>
                Selecciona un documento a la izquierda o crea uno nuevo.
              </p>
            ) : (
              <>
                <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 6 }}>
                  Slug: <code>{editSlug}</code>
                </div>
                <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 6 }}>
                  Título (solo CRM)
                </label>
                <input
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 14,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                    background: c.bgApp,
                    color: c.textPrimary,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
                {esEditorEncargoClausulas(editSlug) ? (
                  verClausulas ? (
                    <>
                      <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 8 }}>
                        Cláusulas del encargo
                      </label>
                      <div
                        style={{ opacity: modClausulas ? 1 : 0.6, pointerEvents: modClausulas ? "auto" : "none" }}
                        title={!modClausulas ? "No tienes permiso para modificar el editor de cláusulas" : undefined}
                      >
                        <EncargoClausulasEditor
                          filas={encargoFilas}
                          onChangeFilas={setEncargoFilas}
                          c={c}
                          theme={theme}
                        />
                      </div>
                    </>
                  ) : (
                    <p style={{ color: c.textMuted, margin: 0, fontSize: 13 }}>
                      No tienes permiso para ver el editor de cláusulas.
                    </p>
                  )
                ) : (
                  <>
                    <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: c.textPrimary, marginBottom: 6 }}>
                      Contenido (se inserta en la plantilla; puedes usar HTML simple)
                    </label>
                    <textarea
                      value={contenido}
                      onChange={(e) => setContenido(e.target.value)}
                      rows={16}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "12px 14px",
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        background: c.bgApp,
                        color: c.textPrimary,
                        fontSize: 14,
                        fontFamily: "ui-monospace, monospace",
                        lineHeight: 1.45,
                        resize: "vertical",
                      }}
                    />
                  </>
                )}
                {(!esEditorEncargoClausulas(editSlug) || (verClausulas && modClausulas)) && (
                <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    disabled={guardando}
                    onClick={guardar}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 10,
                      border: "none",
                      background: c.accentBlue,
                      color: "#fff",
                      fontWeight: 700,
                      cursor: guardando ? "wait" : "pointer",
                      opacity: guardando ? 0.85 : 1,
                    }}
                  >
                    {guardando ? "Guardando…" : "Guardar"}
                  </button>
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 820px) {
          .doc-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
